/**
 * Super-admin dashboard — every tenant on the platform with stats.
 *
 * Three headline counters at the top (tenants / members / open issues), then
 * a sortable table of all tenants with their live metrics. Click into a row
 * to see the per-tenant detail (V2 expansion).
 */

import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { type CSSProperties } from "react";

import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";

type TenantSummary = {
  id: string;
  slug: string;
  name: string;
  plan: string;
  created_at: string;
  phone: string | null;
  mailing_address: string | null;
  billing_email: string | null;
  member_count: number;
  project_count: number;
  issue_count: number;
  open_issue_count: number;
  last_activity_at: string | null;
};

type TenantList = {
  tenants: TenantSummary[];
  total_tenants: number;
  total_members: number;
  total_open_issues: number;
};

export function SuperAdminDashboardPage() {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["superadmin-tenants"],
    queryFn: async () => (await api.get<TenantList>("/superadmin/tenants")).data,
    enabled: isSuperAdmin,
  });

  if (!isSuperAdmin) {
    return (
      <div>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px" }}>
          All tenants
        </h1>
        <p style={{ color: "var(--mute)", marginTop: 8 }}>
          Super-admin only. This is the SaaS owners' cross-tenant view.
        </p>
      </div>
    );
  }

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          All tenants
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Every workspace on the platform with live activity stats. Use{" "}
          <strong style={{ color: "var(--ink)", fontWeight: 600 }}>Tenant management</strong>
          {" "}in the sidebar to add or remove one.
        </p>
      </header>

      {/* Headline counters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginBottom: 32,
        }}
      >
        <Kpi
          label="Tenants"
          value={isLoading ? "…" : String(data?.total_tenants ?? 0)}
          accent="var(--brand-teal, #0F766E)"
        />
        <Kpi
          label="Total members"
          value={isLoading ? "…" : String(data?.total_members ?? 0)}
          accent="var(--accent-purple, #7a3dff)"
        />
        <Kpi
          label="Open issues across platform"
          value={isLoading ? "…" : String(data?.total_open_issues ?? 0)}
          accent="var(--accent-orange, #ff6b00)"
        />
      </div>

      {/* Tenant table */}
      <div style={sectionTitle}>Tenants · {isLoading ? "…" : data?.tenants.length ?? 0}</div>
      {isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : !data || data.tenants.length === 0 ? (
        <p style={{ color: "var(--mute)" }}>
          No tenants yet.{" "}
          <Link to="/superadmin/onboarding" style={{ color: "var(--ink)" }}>
            Onboard the first one.
          </Link>
        </p>
      ) : (
        <div
          style={{
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            background: "var(--canvas)",
            overflowX: "auto",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 800 }}>
            <thead>
              <tr style={{ background: "var(--surface-1)" }}>
                <th style={th}>Tenant</th>
                <th style={th}>Slug</th>
                <th style={th}>Plan</th>
                <th style={th}>Phone</th>
                <th style={th}>Billing email</th>
                <th style={thNum}>Members</th>
                <th style={thNum}>Projects</th>
                <th style={thNum}>Open issues</th>
                <th style={thNum}>Total issues</th>
                <th style={th}>Last activity</th>
                <th style={th}>Created</th>
              </tr>
            </thead>
            <tbody>
              {data.tenants.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => navigate(`/superadmin/tenants/${t.slug}`)}
                  style={{
                    borderTop: "1px solid var(--hairline)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--surface-1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <td style={td}>
                    <strong style={{ color: "var(--ink)" }}>{t.name}</strong>
                  </td>
                  <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12 }}>
                    {t.slug}
                  </td>
                  <td style={td}>
                    <span style={planPill}>{t.plan}</span>
                  </td>
                  <td style={{ ...td, color: t.phone ? "var(--body)" : "var(--mute)" }}>
                    {t.phone ?? "—"}
                  </td>
                  <td style={{ ...td, fontSize: 12, color: t.billing_email ? "var(--body)" : "var(--mute)" }}>
                    {t.billing_email ?? "—"}
                  </td>
                  <td style={tdNum}>{t.member_count}</td>
                  <td style={tdNum}>{t.project_count}</td>
                  <td style={tdNum}>{t.open_issue_count}</td>
                  <td style={tdNum}>{t.issue_count}</td>
                  <td style={{ ...td, color: "var(--mute)" }}>
                    {t.last_activity_at
                      ? new Date(t.last_activity_at).toLocaleString()
                      : "—"}
                  </td>
                  <td style={{ ...td, color: "var(--mute)" }}>
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: string;
}) {
  return (
    <div
      style={{
        background: "var(--canvas)",
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        padding: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: accent,
          opacity: 0.85,
        }}
      />
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: "var(--mute)",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.4px", lineHeight: 1.1 }}>
        {value}
      </div>
    </div>
  );
}

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 12,
};

const th: CSSProperties = {
  textAlign: "left",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  padding: "10px 12px",
  borderBottom: "1px solid var(--hairline)",
};

const thNum: CSSProperties = { ...th, textAlign: "right" };

const td: CSSProperties = {
  padding: "10px 12px",
  fontSize: 13,
  color: "var(--body)",
  verticalAlign: "middle",
};

const tdNum: CSSProperties = {
  ...td,
  textAlign: "right",
  fontFamily: "var(--font-mono)",
  fontSize: 12.5,
  color: "var(--ink)",
};

const planPill: CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  padding: "2px 8px",
  borderRadius: 3,
  background: "var(--surface-2)",
  color: "var(--body-mid)",
};
